/**
 * CMS Service - Comprehensive API integration for Payload CMS
 * Handles all interactions with the backend CMS
 */

import { API_ENDPOINTS, fetchFromAPI, buildQueryString } from '../config/api.js'

/**
 * Activities Service
 */
export const activitiesService = {
  // Get all activities
  getAll: async (params = {}) => {
    const queryString = buildQueryString({ limit: 100, ...params })
    return fetchFromAPI(`${API_ENDPOINTS.activities}${queryString}`)
  },

  // Get single activity by slug
  getBySlug: async (slug) => {
    const queryString = buildQueryString({ where: { slug: { equals: slug } } })
    const response = await fetchFromAPI(`${API_ENDPOINTS.activities}${queryString}`)
    return response.docs?.[0] || null
  },

  // Get featured activities
  getFeatured: async () => {
    const queryString = buildQueryString({
      where: { status: { equals: 'published' } },
      limit: 8,
      sort: '-rating'
    })
    return fetchFromAPI(`${API_ENDPOINTS.activities}${queryString}`)
  },
}

/**
 * Destinations Service
 */
export const destinationsService = {
  // Get all destinations
  getAll: async (params = {}) => {
    const queryString = buildQueryString({ limit: 100, ...params })
    return fetchFromAPI(`${API_ENDPOINTS.destinations}${queryString}`)
  },

  // Get single destination by slug
  getBySlug: async (slug) => {
    const queryString = buildQueryString({ where: { slug: { equals: slug } } })
    const response = await fetchFromAPI(`${API_ENDPOINTS.destinations}${queryString}`)
    return response.docs?.[0] || null
  },

  // Get destinations by region
  getByRegion: async (region) => {
    const queryString = buildQueryString({
      where: { region: { equals: region } },
      limit: 50
    })
    return fetchFromAPI(`${API_ENDPOINTS.destinations}${queryString}`)
  },
}

/**
 * Packages Service
 */
export const packagesService = {
  // Get all packages
  getAll: async (params = {}) => {
    const queryString = buildQueryString({ limit: 100, ...params })
    return fetchFromAPI(`${API_ENDPOINTS.packages}${queryString}`)
  },

  // Get single package by slug
  getBySlug: async (slug) => {
    const queryString = buildQueryString({ where: { slug: { equals: slug } } })
    const response = await fetchFromAPI(`${API_ENDPOINTS.packages}${queryString}`)
    return response.docs?.[0] || null
  },

  // Get featured packages
  getFeatured: async () => {
    const queryString = buildQueryString({
      where: { status: { equals: 'published' } },
      limit: 8,
      sort: '-rating'
    })
    return fetchFromAPI(`${API_ENDPOINTS.packages}${queryString}`)
  },
}

/**
 * Blog Posts Service
 */
export const blogService = {
  // Get all blog posts
  getAll: async (params = {}) => {
    const queryString = buildQueryString({
      limit: 20,
      sort: '-publishedDate',
      ...params
    })
    return fetchFromAPI(`${API_ENDPOINTS.blogPosts}${queryString}`)
  },

  // Get single blog post by slug
  getBySlug: async (slug) => {
    const queryString = buildQueryString({ where: { slug: { equals: slug } } })
    const response = await fetchFromAPI(`${API_ENDPOINTS.blogPosts}${queryString}`)
    return response.docs?.[0] || null
  },
}

/**
 * Testimonials Service
 */
export const testimonialsService = {
  // Get all published testimonials
  getAll: async (params = {}) => {
    const queryString = buildQueryString({
      where: { status: { equals: 'published' } },
      limit: 50,
      sort: '-createdAt',
      ...params
    })
    return fetchFromAPI(`${API_ENDPOINTS.testimonials || `${API_ENDPOINTS.activities.replace('/activities', '/testimonials')}`}${queryString}`)
  },

  // Get featured testimonials for homepage
  getFeatured: async () => {
    const queryString = buildQueryString({
      where: {
        status: { equals: 'published' },
        featured: { equals: true }
      },
      limit: 6,
      sort: '-createdAt'
    })
    return fetchFromAPI(`${API_ENDPOINTS.testimonials || `${API_ENDPOINTS.activities.replace('/activities', '/testimonials')}`}${queryString}`)
  },
}

/**
 * Form Submissions Service
 */
export const formsService = {
  // Submit booking inquiry
  submitBooking: async (bookingData) => {
    try {
      const payload = {
        formType: 'booking',
        name: bookingData.name,
        email: bookingData.email,
        phone: bookingData.phone || '',
        country: bookingData.country || '',
        message: bookingData.message || '',
        bookingData: {
          safariPackages: bookingData.safariPackages?.map(pkg => ({ package: pkg })) || [],
          adventureActivities: bookingData.adventureActivities?.map(act => ({ activity: act })) || [],
          travelServices: bookingData.travelServices?.map(svc => ({ service: svc })) || [],
          startDate: bookingData.startDate || null,
          endDate: bookingData.endDate || null,
          guests: parseInt(bookingData.guests) || 1,
          accommodation: bookingData.accommodation || null,
          budget: bookingData.budget || null,
        },
        submittedAt: new Date().toISOString(),
        status: 'new',
      }

      return await fetchFromAPI(API_ENDPOINTS.formSubmissions, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    } catch (error) {
      console.error('Booking submission error:', error)
      throw error
    }
  },

  // Submit contact form
  submitContact: async (contactData) => {
    try {
      const payload = {
        formType: 'contact',
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone || '',
        subject: contactData.subject || '',
        message: contactData.message || '',
        submittedAt: new Date().toISOString(),
        status: 'new',
      }

      return await fetchFromAPI(API_ENDPOINTS.formSubmissions, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    } catch (error) {
      console.error('Contact form submission error:', error)
      throw error
    }
  },

  // Submit newsletter signup
  submitNewsletter: async (newsletterData) => {
    try {
      const payload = {
        formType: 'newsletter',
        name: newsletterData.name || 'Newsletter Subscriber',
        email: newsletterData.email,
        submittedAt: new Date().toISOString(),
        status: 'new',
      }

      return await fetchFromAPI(API_ENDPOINTS.formSubmissions, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    } catch (error) {
      console.error('Newsletter submission error:', error)
      throw error
    }
  },

  // Submit testimonial
  submitTestimonial: async (testimonialData) => {
    try {
      const payload = {
        formType: 'testimonial',
        name: testimonialData.name,
        email: testimonialData.email,
        message: testimonialData.review || testimonialData.message,
        submittedAt: new Date().toISOString(),
        status: 'new',
      }

      return await fetchFromAPI(API_ENDPOINTS.formSubmissions, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    } catch (error) {
      console.error('Testimonial submission error:', error)
      throw error
    }
  },
}

/**
 * Site Settings Service
 */
export const siteSettingsService = {
  // Get site settings
  get: async () => {
    const siteSettingsUrl = API_ENDPOINTS.activities.replace('/activities', '/globals/site-settings')
    return fetchFromAPI(siteSettingsUrl)
  },
}

/**
 * Media Service
 */
export const mediaService = {
  // Upload media file
  upload: async (file) => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(API_ENDPOINTS.media, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Media upload failed: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Media upload error:', error)
      throw error
    }
  },
}

// Export all services
export default {
  activities: activitiesService,
  destinations: destinationsService,
  packages: packagesService,
  blog: blogService,
  testimonials: testimonialsService,
  forms: formsService,
  siteSettings: siteSettingsService,
  media: mediaService,
}
